// REL_1.1.14 F-262 / S-983 — Marketing footer. Ported from deedo-frontend MarketingFooter.tsx.
// Differences:
//   - Privacy + ToS links cross-domain to {appBaseUrl}/privacy-policy + /terms-of-use
//     (auth-bound legal-text canonical per F-262 spec §1.1 user-direct decision)
//   - REL_1.1.15 S-1101 v2.8: Internal Product/Company links use root paths (`/us/` prefix dropped)
import { Link } from "react-router-dom";
import { Facebook, Youtube, Instagram, Linkedin, Mail, MapPin } from "lucide-react";
import DeedoLogo from "./DeedoLogo";
import AppDownloadBadges from "@/components/common/AppDownloadBadges";

interface Props {
  appBaseUrl: string;
}

const PRODUCT_LINKS = [
  { to: "/for-buyers", label: "For Buyers" },
  { to: "/for-agents", label: "For Agents" },
  { to: "/pricing",    label: "Pricing" },
  { to: "/for-agents#deedo-print", label: "Deedo Print" },
];

// REL_1.1.15E — GH#51 (S-1231): corrected official social handles (operator 2026-07-10).
// Twitter replaced with YouTube (Deedo doesn't use Twitter).
const SOCIAL_LINKS = [
  { href: "https://www.facebook.com/deedoai",          label: "Facebook",  Icon: Facebook },
  { href: "https://www.youtube.com/@deedoai",          label: "YouTube",   Icon: Youtube },
  { href: "https://www.instagram.com/deedo_ai",        label: "Instagram", Icon: Instagram },
  { href: "https://www.linkedin.com/company/deedoai/", label: "LinkedIn",  Icon: Linkedin },
];

export default function MarketingFooter({ appBaseUrl }: Props) {
  const companyLinks = [
    { href: "/about",                                 label: "About Us",        external: false },
    { href: "/contact",                               label: "Contact",         external: false },
    { href: `${appBaseUrl}/privacy-policy`,           label: "Privacy Policy",  external: true },
    { href: `${appBaseUrl}/terms-of-use`,             label: "Terms of Service", external: true },
  ];

  return (
    <footer className="bg-[var(--brand-neutral-dark)] text-gray-300">
      <div className="h-0.5 w-full bg-[var(--brand-secondary)]" aria-hidden="true" />

      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
        <div className="grid gap-10 md:grid-cols-4">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2">
              <DeedoLogo size={28} variant="logo-footer" strokeClassName="text-white" />
              <span className="text-xl font-bold text-white">Deedo</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-gray-400">
              Smart Real Estate. Intelligent Living. Bridging the physical and digital property experience.
            </p>
            <div className="mt-5 flex gap-4">
              {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-gray-500 transition-colors hover:text-[var(--brand-secondary)]"
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </a>
              ))}
            </div>
            {/* REL_1.1.15F F-321 (S-1245): runtime-configurable app-store badges (self-hide until a store
                listing is live + the operator flips the blob flag). */}
            <AppDownloadBadges className="mt-5" />
          </div>

          {/* Product */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Product</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {PRODUCT_LINKS.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-gray-400 transition-colors hover:text-[var(--brand-secondary)]">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Company</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {companyLinks.map((item) =>
                item.external ? (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="text-gray-400 transition-colors hover:text-[var(--brand-secondary)]"
                    >
                      {item.label}
                    </a>
                  </li>
                ) : (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      className="text-gray-400 transition-colors hover:text-[var(--brand-secondary)]"
                    >
                      {item.label}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Get in Touch */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Get in Touch</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                <a
                  href="mailto:support@deedo.ai"
                  className="text-gray-400 transition-colors hover:text-[var(--brand-secondary)]"
                >
                  support@deedo.ai
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                <span className="text-gray-400">
                  {/* REL_1.1.15F F-320 (S-1244): canonical office address (operator-verbatim). */}
                  21701 Stevens Creek Blvd #2129<br />Cupertino, CA 95015-2129
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-6 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} Deedo. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
